import { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { LayoutDashboard, User, Package, MessageCircle, CalendarDays } from 'lucide-react';
import { WeddingVendor } from '../../../types';

export default function VendorLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const [vendor, setVendor] = useState<WeddingVendor | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchVendor = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                navigate('/');
                return;
            }

            const { data: vendorData } = await supabase
                .from('wedding_vendors')
                .select('*')
                .eq('owner_id', session.user.id)
                .single();

            setVendor(vendorData);
            setLoading(false);
        };

        fetchVendor();
    }, [navigate]);

    const menuItems = [
        { icon: LayoutDashboard, label: 'Dashboard', path: '/vendor/dashboard' },
        { icon: User, label: 'Profile', path: '/vendor/profile' },
        { icon: Package, label: 'Packages', path: '/vendor/packages' },
        { icon: CalendarDays, label: 'Bookings', path: '/vendor/bookings' },
        { icon: MessageCircle, label: 'Inquiries', path: '/vendor/inquiries' },
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rose-600"></div>
            </div>
        );
    }

    // If no vendor profile exists yet, just render the child content (likely "Create Profile" page)
    // without the sidebar, as the sidebar requires vendor details.
    if (!vendor) {
        return <Outlet />;
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 pb-8 pt-20"> {/* pt-20 for fixed navbar */}
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar */}
                    <aside className="lg:w-64 flex-shrink-0">
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sticky top-24">
                            <div className="text-center mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                                <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl font-bold text-rose-600 overflow-hidden">
                                    {vendor.images && vendor.images.length > 0 ? (
                                        <img src={vendor.images[0]} alt={vendor.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <span>{vendor.name[0]}</span>
                                    )}
                                </div>
                                <h3 className="font-semibold text-gray-900 dark:text-white truncate" title={vendor.name}>{vendor.name}</h3>
                                <p className="text-xs text-gray-500 mt-1 capitalize">{vendor.category?.name || 'Vendor'}</p>
                            </div>

                            <nav className="space-y-1">
                                {menuItems.map((item) => {
                                    const isActive = location.pathname === item.path;
                                    return (
                                        <button
                                            key={item.path}
                                            onClick={() => navigate(item.path)}
                                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive
                                                    ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-600'
                                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                                                }`}
                                        >
                                            <item.icon size={20} />
                                            {item.label}
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <main className="flex-1 min-w-0">
                        <Outlet context={{ vendor }} />
                    </main>
                </div>
            </div>
        </div>
    );
}
